import { useMemo } from 'react'
import dayjs from 'dayjs'
import * as yup from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useSnackbar } from 'notistack'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import LoadingButton from '@mui/lab/LoadingButton'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'

import DatePickerInput from '@/components/ui/inputs/DatePickerInput'
import PhoneNumberInputController from '@/components/ui/inputs/PhoneNumberInputController'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import TextInputController from '@/components/ui/inputs/TextInputController'
import { Patient } from '@/core/models/patient.model'
import { useUpdatePatientMutation } from '@/core/services/patient'
import { Gender } from '@/core/types/shared'
import { toUtcMidnight } from '@/utils/dateUtils'
import { getApiErrorMessage } from '@/utils/errors'
import { transformObject } from '@/utils/helpers'
import { getGenderOptions } from '@/utils/patient'

const buildIdentitySchema = (requiredMessage: string) =>
  yup.object({
    name: yup.string().trim().required(requiredMessage),
    surname: yup.string().trim().required(requiredMessage),
    birthday: yup.date().nullable().max(new Date()).defined(),
    gender: yup.string().defined(),
    phone: yup.string().defined(),
  })

type IdentityFormValues = yup.InferType<ReturnType<typeof buildIdentitySchema>>

type ReceptionIdentityFormProps = { patient: Patient; onSaved: () => void }

/**
 * The few identity fields the reception completes during the wait (an emergency patient arrives nameless).
 * Sends the same subset as the usual patient panel: the API merges it with the rest of the record.
 */
export function ReceptionIdentityForm({ patient, onSaved }: ReceptionIdentityFormProps) {
  const { i18n } = useLingui()
  const { enqueueSnackbar } = useSnackbar()
  const [updatePatient, { isLoading }] = useUpdatePatientMutation()
  const schema = useMemo(() => buildIdentitySchema(t`This field is required`), [i18n.locale])
  const genderOptions = useMemo(() => getGenderOptions().map((option) => ({ ...option, label: i18n._(option.label) })), [i18n, i18n.locale])

  const form = useForm<IdentityFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: patient.userInfo.name ?? '',
      surname: patient.userInfo.surname ?? '',
      birthday: patient.userInfo.birthday ? new Date(patient.userInfo.birthday) : null,
      gender: patient.userInfo.gender ?? '',
      phone: patient.contactInfo?.phone ?? '',
    },
  })

  const save = async (values: IdentityFormValues) => {
    try {
      await updatePatient({
        id: patient._id,
        userInfo: transformObject({ name: values.name, surname: values.surname, gender: values.gender as Gender, birthday: values.birthday?.toISOString() }),
        contactInfo: transformObject({ phone: values.phone }),
      }).unwrap()
      enqueueSnackbar(t`Identity saved`, { variant: 'success' })
      onSaved()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t`The identity could not be saved`), { variant: 'error' })
    }
  }

  return (
    <Stack component="form" spacing={3} onSubmit={form.handleSubmit(save)} noValidate>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextInputController control={form.control} name="name" label={t`First name`} fullWidth />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextInputController control={form.control} name="surname" label={t`Last name`} fullWidth />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            control={form.control}
            name="birthday"
            render={({ field, fieldState }) => (
              <DatePickerInput
                disableFuture
                fullWidth
                name={field.name}
                label={t`Birthday`}
                onChange={(date) => form.setValue('birthday', toUtcMidnight(date)?.toDate() ?? null)}
                value={field.value && dayjs(field.value).isValid() ? dayjs(field.value) : null}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SelectInputController control={form.control} name="gender" id="reception-gender" label={t`Gender`} options={genderOptions} fullWidth />
        </Grid>
        <Grid item xs={12}>
          <PhoneNumberInputController control={form.control} name="phone" label={t`Phone`} />
        </Grid>
      </Grid>
      <LoadingButton type="submit" variant="contained" size="large" loading={isLoading}>
        <Trans>Save and go to vital signs</Trans>
      </LoadingButton>
    </Stack>
  )
}
